/**
 * The business register mirror, as a list you can work.
 *
 * Split out of `AriregisterSection.tsx`, which was 657 lines holding this, the
 * filter panel, the sync status and the routes (AGENTS.md §4).
 *
 * The table is no longer a fixed set of tracks. Which columns it draws comes
 * from `columns.tsx` filtered by the operator's own choice (`useColumnPrefs`),
 * every head that names an indexed column sorts, and a selection wider than the
 * screen scrolls the card sideways rather than squeezing sixteen unreadable
 * columns into it - see `listGrid.ts` for the floor it scrolls below.
 *
 * This file owns the STATE and nothing else draws it twice. Both background jobs
 * behind the register - the mirror's sync and the web need ranking pass - are
 * polled once here and handed down to `ListHead` (which reports on them) and
 * `WebNeedBands` (which filters by the ranking's own bands). They used to be two
 * self-contained cards in the page head with a poll each, which is how the same
 * screen ended up able to show two different answers to the same question.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ChevronLeft, ChevronRight, Loader2, Megaphone } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  listEntities,
  type ArListParams, type ArListResult, type ArListRow, type ArOrder, type ArSort,
} from "../../../lib/ariregister";
import { EMTAK_ACTIVITY_OPTIONS, activityLabel } from "../../../lib/emtak";
import {
  EMPTY_AUDIENCE_DRAFT, EMPTY_AUDIENCE_FILTER,
  advancedFilterCount, applyPatch, audienceToParams, hasAnyFilter, hasFilterBesidesWebNeed,
  registerAudience, registeredRange, toIsoDate,
  type AudienceDraft, type AudienceFilter,
} from "../../../lib/audienceFilter";
import { audienceChips, draftPatchFor, type AudienceChip } from "../../../lib/audienceChips";
import { Btn, EmptyState, SortHead, fmtNum, listCard } from "../../ui";
import type { CallTarget } from "../../telephony/callState";
import { QueueBar, QueuePick, QueuePickAll, useQueuePicks, type QueuePicks } from "../../telephony/queue";
import { PopHost } from "../../shell/Pop";
import { AriregisterFilters } from "./AriregisterFilters";
import { firstNumber } from "./ContactCell";
import type { ProspectPreset } from "./AriregisterStats";
import { ColumnPicker } from "./ColumnPicker";
import { columnTracks, type ArColumn, type ColumnCtx } from "./columns";
import { useColumnPrefs } from "./useColumnPrefs";
import { EntityRowMenu } from "./EntityRowMenu";
import { ListHead } from "./ListHead";
import { useSyncStatus, useWebNeedPass } from "./data";
import { WebNeedBands } from "./webneed";
import { DomainCheckDialog } from "./domain";
import { NewCampaignModal } from "../marketing/NewCampaignModal";

const PAGE_SIZE = 25;

/**
 * What dialling this row means. Null when there is nothing to dial - the row
 * still gets a (disabled) box, so the column has no holes in it.
 *
 * `firstNumber` for the same reason the contact cell uses it: the register
 * stores a company's numbers as one semicolon-joined string, and the one shown
 * has to be the one called.
 */
function targetOf(row: ArListRow): CallTarget | null {
  const phone = row.phone ? firstNumber(row.phone) : "";
  if (!phone) return null;
  return { phone, source: "ariregister", companyName: row.name, regCode: row.regCode };
}

export function AriregisterList({ user }: { user: StaffUser }) {
  const t = useT();
  const canManage = userCanPlatform(user, "manage:ariregister");
  // A separate right from the register's own. Reading company data and writing
  // to those companies are different responsibilities, which is the whole
  // reason `manage:crm.marketing` exists as its own key.
  const canMarket = userCanPlatform(user, "manage:crm.marketing");
  const navigate = useNavigate();
  const columns = useColumnPrefs();

  // One committed filter object (see lib/audienceFilter.ts for why it is one
  // object and not twenty hooks), plus the typing buffers for the four
  // debounced fields. A draft is never a filter until the debounce commits it.
  const [filters, setFilters] = useState<AudienceFilter>(EMPTY_AUDIENCE_FILTER);
  const [draft, setDraft] = useState<AudienceDraft>(EMPTY_AUDIENCE_DRAFT);
  const patch = useCallback((p: Partial<AudienceFilter>) => setFilters((f) => applyPatch(f, p)), []);

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [sort, setSort] = useState<ArSort>("name");
  const [order, setOrder] = useState<ArOrder>("asc");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<ArListResult | null>(null);
  const [loading, setLoading] = useState(true);
  // A filtered register page IS the cold-calling list - it is where most of
  // these numbers come from in the first place.
  const picks = useQueuePicks();
  // Row whose domain check is open. Held here (not per row) so only one lookup
  // can be in flight and closing the modal fully unmounts it.
  const [dnsRow, setDnsRow] = useState<ArListRow | null>(null);
  const [newCampaign, setNewCampaign] = useState(false);

  // Search + amount debounce (350 ms) - avoid a request on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      patch({
        q: draft.q.trim(),
        capitalMin: draft.capitalMin.trim(),
        capitalMax: draft.capitalMax.trim(),
        revenueMin: draft.revenueMin.trim(),
        revenueMax: draft.revenueMax.trim(),
        employeesMin: draft.employeesMin.trim(),
        employeesMax: draft.employeesMax.trim(),
        debtMin: draft.debtMin.trim(),
        webNeedMin: draft.webNeedMin.trim(),
        webNeedMax: draft.webNeedMax.trim(),
      });
    }, 350);
    return () => clearTimeout(id);
  }, [draft, patch]);

  // Filter change -> back to page 1.
  useEffect(() => {
    setPage(1);
  }, [filters, sort, order]);

  const params = useMemo<ArListParams>(
    () => ({ ...audienceToParams(filters), sort, order, page, pageSize: PAGE_SIZE }),
    [filters, sort, order, page],
  );

  // Activity combobox options on three levels - section / division / activity
  // class (indented), all searchable by name.
  const activityOptions = useMemo(
    () =>
      EMTAK_ACTIVITY_OPTIONS.map((o) => ({
        value: o.value,
        label: o.label,
        hint: o.hint,
        prefix:
          o.level === 0 ? (
            <span className="font-semibold text-[var(--color-primary)]">&#9656;</span>
          ) : o.level === 1 ? (
            <span className="w-2.5" />
          ) : (
            <span className="w-2.5 shrink-0 border-l border-[var(--crm-hair)] pl-3" />
          ),
      })),
    [],
  );

  // Bumped to refetch the same query - what a finished web need pass needs, as
  // it changes the ORDER of a page without changing any of its parameters.
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  // Both background jobs are polled ONCE, here, and handed down. The head, the
  // manage menu and the band filter all read these numbers, and three copies of
  // the same poll would let one part of the screen contradict another.
  const sync = useSyncStatus({
    started: t("ariregister.sync.started"),
    failed: t("ariregister.sync.failed"),
    licence: t("ariregister.sync.licence"),
  });
  const pass = useWebNeedPass(reload);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listEntities(params)
      .then((r) => alive && setData(r))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [params, reloadKey]);

  const hasFilters = hasAnyFilter(filters);
  const otherFilters = hasFilterBesidesWebNeed(filters);

  function clearFilters() {
    setDraft(EMPTY_AUDIENCE_DRAFT);
    setFilters(EMPTY_AUDIENCE_FILTER);
  }

  // Prospect preset from the stats dialog -> apply the list filters (no website
  // on file + has email; plus a revenue or recency bound) and open advanced
  // filters. Both halves are set in one go: clearing and then patching would
  // leave the draft and the filter disagreeing for one debounce cycle.
  function applyProspectPreset(preset: ProspectPreset) {
    const next: AudienceFilter = { ...EMPTY_AUDIENCE_FILTER, hasWebsite: "false", hasEmail: true };
    const nextDraft: AudienceDraft = { ...EMPTY_AUDIENCE_DRAFT };
    if (preset === "noSiteRevenue") {
      next.revenueMin = "1";
      nextDraft.revenueMin = "1";
    }
    if (preset === "newNoSite") {
      const to = new Date();
      const from = new Date();
      from.setFullYear(from.getFullYear() - 1);
      next.registeredFrom = toIsoDate(from, false);
      next.registeredTo = toIsoDate(to, true);
    }
    setDraft(nextDraft);
    setFilters(next);
    setShowAdvanced(true);
  }

  // Active filter chips - quick overview + one-by-one removal. Built by the
  // shared module rather than here, so the campaign audience builder cannot word
  // the same filter differently; the readable activity name is the one thing it
  // cannot resolve on its own.
  const chips = audienceChips(filters, t).map((c) =>
    c.key === "emtak" ? { ...c, label: activityLabel(filters.emtak) } : c,
  );

  // Removing a chip has to clear the typing buffer too, or the debounce puts the
  // value straight back a moment later.
  const removeChip = (chip: AudienceChip) => {
    const blank = draftPatchFor(chip.clear);
    if (Object.keys(blank).length) setDraft((d) => ({ ...d, ...blank }));
    patch(chip.clear);
  };

  const cols = columnTracks(columns.shown, picks !== null);
  const pageTargets = (data?.rows ?? []).flatMap((r) => {
    const target = targetOf(r);
    return target ? [{ key: r.regCode, target }] : [];
  });

  /**
   * A click on a column head. The active column flips direction; a new one
   * starts in its own natural direction, which is the whole reason the columns
   * carry `descFirst`: clicking "turnover" and getting the smallest companies
   * first is never what was meant, and clicking a name column and getting the
   * end of the alphabet first is just as wrong.
   */
  function sortByColumn(column: ArColumn) {
    if (!column.sort) return;
    if (column.sort === sort) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
      return;
    }
    setSort(column.sort);
    setOrder(column.descFirst ? "desc" : "asc");
  }

  const cellCtx: ColumnCtx = { t, q: filters.q };

  return (
    // One open menu at a time across every row.
    <PopHost>
      <ListHead canManage={canManage} sync={sync} pass={pass} onApplyProspect={applyProspectPreset} />

      <AriregisterFilters
        filters={filters}
        draft={draft}
        setDraft={setDraft}
        patch={patch}
        showAdvanced={showAdvanced}
        onToggleAdvanced={() => setShowAdvanced((v) => !v)}
        advancedCount={advancedFilterCount(filters)}
        activityOptions={activityOptions}
        regRange={registeredRange(filters)}
        onRegRange={(r) =>
          patch({
            registeredFrom: r ? toIsoDate(r.from, false) : null,
            registeredTo: r ? toIsoDate(r.to, true) : null,
          })
        }
        chips={chips}
        // The most valuable single thing this page can do, and it sits in the
        // chip row on purpose: that row IS the selection, so "make a campaign
        // from this" is the action on the thing beside it. It appears only when
        // something is filtered - a campaign over the unfiltered register is a
        // real choice, but not one to offer as a button next to a chip.
        actions={
          canMarket ? (
            <Btn small tone="brand" onClick={() => setNewCampaign(true)}>
              <Megaphone className="size-3.5" />
              {t("ariregister.makeCampaign")}
            </Btn>
          ) : null
        }
        onRemoveChip={removeChip}
        onClear={clearFilters}
        hasFilters={hasFilters}
        sort={sort}
        order={order}
        onSort={(s, o) => {
          setSort(s);
          setOrder(o);
        }}
        columnPicker={
          <ColumnPicker
            visible={columns.visible}
            onToggle={columns.toggle}
            onReset={columns.reset}
            changed={columns.changed}
            t={t}
          />
        }
      />

      {/* The band filter and the size of what it selected, on one line straight
          above the table. Both used to be somewhere else: the counts in a status
          bar in the page head where they could only be read, the total in the
          table's footer where you had to scroll past 25 rows to learn how many
          there were. */}
      <div className="crm-bandbar">
        <WebNeedBands
          // Register-wide counts, so they are shown only while nothing else is
          // narrowing the list - see `hasFilterBesidesWebNeed`. The buttons keep
          // working either way; they just stop promising a number they cannot
          // keep once a county or a turnover floor is also in play.
          counts={otherFilters ? null : pass.progress?.bands ?? null}
          min={filters.webNeedMin}
          max={filters.webNeedMax}
          onPick={(bounds) => {
            const next = { webNeedMin: bounds?.min ?? "", webNeedMax: bounds?.max ?? "" };
            // The draft too, or the 350 ms debounce puts the old bounds straight
            // back - the same trap `removeChip` above has to step around.
            setDraft((d) => ({ ...d, ...next }));
            patch(next);
          }}
        />
        {data ? (
          <span className="crm-bandbar-n crm-tnum">
            {t("ariregister.resultsCount", { total: fmtNum(data.total) })}
            {loading ? <Loader2 className="ml-1.5 inline size-3.5 animate-spin" /> : null}
          </span>
        ) : null}
      </div>

      <QueueBar picks={picks} />

      <div className="crm-card" style={listCard(cols)}>
        {loading && !data ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : !data || data.rows.length === 0 ? (
          <EmptyState icon={Building2} title={t("ariregister.empty")} body={t("ariregister.emptyBody")} />
        ) : (
          <>
            <div className="crm-list-head" style={{ gridTemplateColumns: cols, paddingTop: 22 }}>
              {picks ? <QueuePickAll picks={picks} entries={pageTargets} /> : null}
              {columns.shown.map((c) =>
                c.sort ? (
                  <SortHead
                    key={c.key}
                    label={t(`ariregister.col.${c.label}`)}
                    column={c.sort}
                    sort={sort}
                    order={order}
                    onSort={() => sortByColumn(c)}
                  />
                ) : (
                  <span key={c.key} className="crm-trunc">
                    {t(`ariregister.col.${c.label}`)}
                  </span>
                ),
              )}
              <span />
            </div>

            <div className="crm-rows pt-0">
              {data.rows.map((r) => (
                <EntityRow
                  key={r.regCode}
                  row={r}
                  cols={cols}
                  shown={columns.shown}
                  ctx={cellCtx}
                  picks={picks}
                  onCheckDomain={() => setDnsRow(r)}
                />
              ))}
            </div>

            {/* No total down here any more: it is stated once, above the table,
                where it is read before the rows rather than after them. */}
            <div className="crm-tfoot">
              <span className="crm-push flex items-center gap-2.5">
                <span className="crm-mut crm-tnum">
                  {t("ariregister.pageOf", { page: data.page, pageCount: data.pageCount })}
                </span>
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("ariregister.prev")}
                </Btn>
                <Btn small disabled={page >= data.pageCount} onClick={() => setPage((p) => p + 1)}>
                  {t("ariregister.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      {/* Domain check for one row - keyed so reopening a different company
          starts a fresh lookup instead of showing the previous one. */}
      {dnsRow ? (
        <DomainCheckDialog
          key={dnsRow.regCode}
          regCode={dnsRow.regCode}
          companyName={dnsRow.name}
          webUrl={dnsRow.webUrl}
          open
          onOpenChange={(v) => !v && setDnsRow(null)}
          t={t}
        />
      ) : null}

      {/* The filter travels as the audience the server will store, built by the
          SAME function the campaign editor's own audience panel uses. Two ways
          of turning this screenful into an audience would differ silently, and
          the symptom would be a campaign selecting slightly more or slightly
          fewer companies than the page it was started from. */}
      {newCampaign ? (
        <NewCampaignModal
          seed={{
            source: "register",
            filter: registerAudience(filters),
            // Companies, not mailable addresses. The campaign's own preview
            // answers the second question, and it is a much smaller number.
            count: data?.total ?? null,
            chips: chips.map((c) => c.label),
          }}
          onClose={() => setNewCampaign(false)}
          onCreated={(id) => navigate(`/marketing/campaigns/${id}`)}
          onError={() => setNewCampaign(false)}
        />
      ) : null}
    </PopHost>
  );
}

function EntityRow({
  row,
  cols,
  shown,
  ctx,
  picks,
  onCheckDomain,
}: {
  row: ArListRow;
  /** The same track string the head uses - handed down rather than recomputed,
   *  so a head and its column can never be one track apart. */
  cols: string;
  shown: ArColumn[];
  ctx: ColumnCtx;
  picks: QueuePicks | null;
  onCheckDomain: () => void;
}) {
  return (
    // A div, not a link: the row ends in a menu button, and interactive content
    // inside an <a> is invalid HTML and swallows the button's clicks. The name
    // link stretches an ::after over the whole row instead, so the row stays
    // clickable end to end and the button still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: cols }}>
      {picks ? <QueuePick picks={picks} id={row.regCode} target={targetOf(row)} /> : null}

      {/* A Fragment, not a wrapper element: the cell's own element has to BE the
          grid item. A wrapper around it would take the item's blockification
          with it, and every `crm-trunc` cell inside would go back to being an
          inline box - which does not clip, so the ellipsis would quietly stop
          working in half the columns. */}
      {shown.map((c) => (
        <Fragment key={c.key}>{c.cell(row, ctx)}</Fragment>
      ))}

      <EntityRowMenu row={row} onCheckDomain={onCheckDomain} />
    </div>
  );
}
